import { clsx } from 'clsx'
import type { CSSProperties, ReactNode } from 'react'

/**
 * The r5 icon system (APP-DESIGN-DIRECTION 2.5), drawn in-house on a 24px grid: no icon package, no sprite fetch.
 *
 * `Glyph` is the brand set: chunky rounded geometry in two states that share one silhouette.
 *   rest    the silhouette as a bold 2.5px rounded outline over a 16% fill of the same colour, details stroked
 *   active  the silhouette filled solid, details knocked out in `--r5-glyph-knock` (white by default; set it to the
 *           surface colour the glyph sits on, for example the tint-navy nav pill)
 * Skills are told apart by shape, never by new hues. `Icon` is the utility set (search, close, caret ...), 2px strokes.
 * Both are `aria-hidden` unless given a `label`. Direction-bearing utility icons flip in Arabic (`flip`); skill, band,
 * streak and play glyphs never do.
 */

type Part = { d: string; kind: 'shape' | 'line' | 'detail' | 'dot' }
const shape = (d: string): Part => ({ d, kind: 'shape' })
const line = (d: string): Part => ({ d, kind: 'line' })
const detail = (d: string): Part => ({ d, kind: 'detail' })
const dot = (d: string): Part => ({ d, kind: 'dot' })

const roundRect = (x: number, y: number, w: number, h: number, r: number) =>
  `M${x + r} ${y}h${w - 2 * r}a${r} ${r} 0 0 1 ${r} ${r}v${h - 2 * r}a${r} ${r} 0 0 1 -${r} ${r}h-${w - 2 * r}a${r} ${r} 0 0 1 -${r} -${r}v-${h - 2 * r}a${r} ${r} 0 0 1 ${r} -${r}z`
const circle = (cx: number, cy: number, r: number) => `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 -${2 * r} 0z`

const GLYPHS = {
  // The navigation set is drawn in the r5 geometry: the chevron roof, the "{>" lockup, the band arc, a route that ends in
  // a chevron, pill switches. None of them is a stock document, chat, bar chart, calendar or slider icon.
  home: [shape('M12 3.2l8.6 7.3v3.3L12 6.6l-8.6 7.2v-3.3z'), shape('M6.4 13.5 12 8.9l5.6 4.6V19a1.8 1.8 0 0 1-1.8 1.8H8.2A1.8 1.8 0 0 1 6.4 19z'), detail('M10.4 20.6v-2.8a1.6 1.6 0 0 1 3.2 0v2.8')],
  practice: [shape(roundRect(4.5, 3, 15, 18, 3.4)), detail('M10.2 8.4l4.6 3.6-4.6 3.6')],
  talk: [shape('M6 3.6h12A3 3 0 0 1 21 6.6v7.2a3 3 0 0 1-3 3h-6.4l-4.8 3.8v-3.8H6a3 3 0 0 1-3-3V6.6a3 3 0 0 1 3-3z'), detail('M9.6 7.2c-1.1 0-1.7.6-1.7 1.6v.4c0 .8-.4 1.2-1.1 1.4.7.2 1.1.6 1.1 1.4v.4c0 1 .6 1.6 1.7 1.6M13 7.6l3.6 2.8-3.6 2.8')],
  results: [line('M4.9 17.1A8.2 8.2 0 1 1 19.1 17.1'), line('M12 13l4.1-4.4'), shape(circle(12, 13, 2.5))],
  plan: [shape(circle(5.2, 18.4, 2.4)), line('M7.6 18.4h2.6a2.8 2.8 0 0 0 2.8-2.8V8.6a2.8 2.8 0 0 1 2.8-2.8h.4'), shape('M16.6 2.6l5 3.2-5 3.2v-2.1l1.9-1.1-1.9-1.1z')],
  listening: [line('M4 15.5V12a8 8 0 0 1 16 0v3.5'), shape('M4 14h1.6A1.6 1.6 0 0 1 7.2 15.6v3.2a1.6 1.6 0 0 1-1.6 1.6h-.6a1 1 0 0 1-1-1z'), shape('M20 14h-1.6a1.6 1.6 0 0 0-1.6 1.6v3.2a1.6 1.6 0 0 0 1.6 1.6h.6a1 1 0 0 0 1-1z')],
  reading: [shape('M3 5.6c2.9-1 6.1-.6 9 1.3 2.9-1.9 6.1-2.3 9-1.3V19c-2.9-1-6.1-.6-9 1.3-2.9-1.9-6.1-2.3-9-1.3z'), detail('M12 7v13')],
  writing: [shape('M16.1 3.9a2.2 2.2 0 0 1 3.1 0l.9.9a2.2 2.2 0 0 1 0 3.1L9.2 18.8 4 20l1.2-5.2z'), detail('M14 6.1l3.9 3.9')],
  speaking: [shape(roundRect(9, 3, 6, 11.5, 3)), line('M5.6 11.4a6.4 6.4 0 0 0 12.8 0M12 17.8V21')],
  mock: [shape(circle(12, 13.5, 7.6)), line('M9.8 2.8h4.4M12 2.8v3'), detail('M12 9.6v4.2l2.6 1.6')],
  band: [line('M8.2 4.4c-1.7 0-2.6.9-2.6 2.6v2.1c0 1.5-.8 2.4-2.3 2.9 1.5.5 2.3 1.4 2.3 2.9V17c0 1.7.9 2.6 2.6 2.6'), line('M13.4 6.6l7 5.4-7 5.4')],
  target: [line('M20 3.8v16.4'), shape('M4.5 6.2l11 5.8-11 5.8v-3.6l5.4-2.2-5.4-2.2z')],
  streak: [shape('M12 2.8l7 6.4v4.4l-7-6.3-7 6.3V9.2z'), shape('M12 10.4l7 6.4v4.4l-7-6.3-7 6.3v-4.4z')],
  achievement: [line('M8.4 13.8 7 21l5-2.6 5 2.6-1.4-7.2'), shape(circle(12, 9.2, 6.2)), detail('M9.6 9.4l1.7 1.7 3.2-3.3')],
  leaderboard: [shape('M9 6.5h6a.6.6 0 0 1 .6.6V20.6H8.4V7.1a.6.6 0 0 1 .6-.6z'), shape('M3.6 11.5h4.8v9.1H3a.6.6 0 0 1-.6-.6v-7.3a1.2 1.2 0 0 1 1.2-1.2z'), shape('M15.6 9.5h4.8a1.2 1.2 0 0 1 1.2 1.2v9.3a.6.6 0 0 1-.6.6h-5.4z'), detail('M11.3 10.6l.9-.6v4')],
  profile: [shape(circle(12, 7.6, 4)), shape('M4.4 20.4c.8-3.9 3.8-6.2 7.6-6.2s6.8 2.3 7.6 6.2z')],
  settings: [shape(roundRect(3, 4, 18, 7, 3.5)), shape(roundRect(3, 13, 18, 7, 3.5)), dot(circle(16.5, 7.5, 2)), dot(circle(7.5, 16.5, 2))],
  overview: [shape(roundRect(4, 4, 6.6, 6.6, 1.6)), shape(roundRect(13.4, 4, 6.6, 6.6, 1.6)), shape(roundRect(4, 13.4, 6.6, 6.6, 1.6)), shape(roundRect(13.4, 13.4, 6.6, 6.6, 3.3))],
  tests: [shape('M7.2 3h6.9L19 7.9V19a2 2 0 0 1-2 2H7.2a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z'), detail('M14 3.4v4.4h4.6M8.8 12.6h6.6M8.8 16.4h4')],
  questions: [line('M3.2 15V6.2a2.2 2.2 0 0 1 2.2-2.2h10.8'), shape(roundRect(6.6, 7.2, 14.2, 13.2, 2.4)), detail('M11.4 11.9a2.1 2.1 0 1 1 3 1.9c-.6.3-.9.7-.9 1.3M13.5 17.3v.1')],
  students: [shape(circle(9, 7.6, 3.6)), shape('M2.6 20.2c.6-3.4 3.2-5.6 6.4-5.6s5.8 2.2 6.4 5.6z'), line('M15.4 4.2a3.6 3.6 0 0 1 0 6.8M18 14.9c1.9.8 3.1 2.6 3.4 5.3')],
  submissions: [shape('M5.4 4h13.2a1.5 1.5 0 0 1 1.4 1.1l1.6 7.4V19a1.6 1.6 0 0 1-1.6 1.6H4A1.6 1.6 0 0 1 2.4 19v-6.5L4 5.1A1.5 1.5 0 0 1 5.4 4z'), detail('M2.6 12.6h4.9l1.6 2.6h5.8l1.6-2.6h4.9')],
  review: [shape(roundRect(5, 4.4, 14, 16.6, 2.4)), line('M9.2 3h5.6'), detail('M8.6 13l2.5 2.5 4.4-4.7')],
  employees: [shape(roundRect(3, 6, 18, 14, 2.4)), line('M10 3.4h4'), detail('M6.8 16.6c.4-1.3 1.3-2 2.4-2s2 .7 2.4 2M9.2 12.6a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2zM14.6 10.6h3.2M14.6 14.2h2.4')],
  announcements: [shape('M4 9.4h2.8L16 4.6v14.8l-9.2-4.8H4a1 1 0 0 1-1-1v-3.2a1 1 0 0 1 1-1z'), line('M7 14.8l1.3 5.4h2.5l-.9-4.6M19.2 9.4a3.6 3.6 0 0 1 0 5.2')],
  support: [shape(circle(12, 12, 8.8)), detail(`${circle(12, 12, 3.4)}M5.8 5.8l3.8 3.8M18.2 5.8l-3.8 3.8M5.8 18.2l3.8-3.8M18.2 18.2l-3.8-3.8`)],
  report: [line('M5.6 21V3.8'), shape('M5.6 4h11.6l-2.2 4.2 2.2 4.2H5.6z')],
  search: [shape(circle(10.6, 10.6, 6.6)), line('M20 20l-4.6-4.6')],
} satisfies Record<string, Part[]>

export type GlyphName = keyof typeof GLYPHS

export interface GlyphProps {
  name: GlyphName
  state?: 'rest' | 'active'
  size?: number
  /** Accessible name. Without it the glyph is decorative (`aria-hidden`). */
  label?: string
  className?: string
  style?: CSSProperties
}

export function Glyph({ name, state = 'rest', size = 24, label, className, style }: GlyphProps) {
  const active = state === 'active'
  const parts = GLYPHS[name] as Part[]
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={clsx('r5-glyph', `r5-glyph--${name}`, active && 'r5-glyph--active', className)}
      style={style}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : 'true'}
      role={label ? 'img' : undefined}
      aria-label={label}
      focusable="false"
      data-glyph={name}
      data-state={state}
    >
      {parts.map((part, index) => {
        // Rest is bold and two-tone (a 2.5px outline over a soft fill of the same colour), so a glyph never reads as a thin
        // stock line icon; active is solid with the details knocked out.
        if (part.kind === 'shape') {
          return <path key={index} d={part.d} fill="currentColor" fillOpacity={active ? 1 : 0.16} stroke="currentColor" strokeWidth={active ? 2 : 2.5} />
        }
        if (part.kind === 'line') return <path key={index} d={part.d} stroke="currentColor" strokeWidth={active ? 3 : 2.6} />
        if (part.kind === 'dot') return <path key={index} d={part.d} fill={active ? 'var(--r5-glyph-knock, #fff)' : 'currentColor'} />
        return <path key={index} d={part.d} stroke={active ? 'var(--r5-glyph-knock, #fff)' : 'currentColor'} strokeWidth={active ? 2.4 : 2.3} />
      })}
    </svg>
  )
}

/** A glyph in a 36px tile (rows, menus, test cards): tint-navy with the rest glyph, or navy with the white active glyph. */
export function GlyphTile({ name, active = false, size = 36, className }: { name: GlyphName; active?: boolean; size?: number; className?: string }) {
  return (
    <span className={clsx('r5-gtile', active && 'r5-gtile--active', className)} style={{ ['--r5-gtile' as string]: `${size}px` }} aria-hidden="true">
      <Glyph name={name} state={active ? 'active' : 'rest'} size={Math.round(size * 0.56)} />
    </span>
  )
}

/* ---------------------------------------------------------------- utility icons ---------------------------------------------------------------- */

const ICONS = {
  search: <><path d={circle(10.6, 10.6, 6.4)} /><path d="M20 20l-4.6-4.6" /></>,
  close: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />,
  more: <><circle cx="5.5" cy="12" r="1.7" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.7" fill="currentColor" stroke="none" /><circle cx="18.5" cy="12" r="1.7" fill="currentColor" stroke="none" /></>,
  caret: <path d="M7 10l5 5 5-5" />,
  'chevron-end': <path d="M9.5 5.5l6.5 6.5-6.5 6.5" />,
  'chevron-start': <path d="M14.5 5.5L8 12l6.5 6.5" />,
  check: <path d="M5 12.6l4.4 4.4L19 7.4" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  filter: <path d="M4 6.5h16M7 12h10M10 17.5h4" />,
  sort: <path d="M8 5v14M4.5 8.5 8 5l3.5 3.5M16 19V5M12.5 15.5 16 19l3.5-3.5" />,
  upload: <path d="M12 15V4.5M7.5 9 12 4.5 16.5 9M5 15v3.4A1.6 1.6 0 0 0 6.6 20h10.8a1.6 1.6 0 0 0 1.6-1.6V15" />,
  download: <path d="M12 4.5V15M7.5 10.5 12 15l4.5-4.5M5 15v3.4A1.6 1.6 0 0 0 6.6 20h10.8a1.6 1.6 0 0 0 1.6-1.6V15" />,
  copy: <><path d={roundRect(8.5, 8.5, 12, 12, 2)} /><path d="M15.5 8.5V5.6a2 2 0 0 0-2-2H5.6a2 2 0 0 0-2 2v7.9a2 2 0 0 0 2 2h2.9" /></>,
  edit: <path d="M15.9 4.1a2.2 2.2 0 0 1 3.1 0l.9.9a2.2 2.2 0 0 1 0 3.1L9 19l-5 1 1-5z" />,
  trash: <path d="M4.5 7h15M9.8 4h4.4M6.5 7l1 12.4A1.6 1.6 0 0 0 9.1 21h5.8a1.6 1.6 0 0 0 1.6-1.6L17.5 7M10 11v6M14 11v6" />,
  link: <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />,
  play: <path d="M8 5.2v13.6L18.6 12z" fill="currentColor" />,
  pause: <path d="M8.5 5.5v13M15.5 5.5v13" />,
  bell: <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.6 2H4.4zM10 20.6a2.1 2.1 0 0 0 4 0" />,
  calendar: <><path d={roundRect(3.5, 5, 17, 15.5, 2.4)} /><path d="M8 3v4M16 3v4M3.5 10h17" /></>,
  clock: <><path d={circle(12, 12, 8.5)} /><path d="M12 7.6V12l3 1.9" /></>,
  eye: <><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" /><path d={circle(12, 12, 2.8)} /></>,
  lock: <><path d={roundRect(5, 10.5, 14, 10, 2.2)} /><path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" /></>,
  'arrow-end': <path d="M4.5 12h15M13.5 6l6 6-6 6" />,
  'arrow-start': <path d="M19.5 12h-15M10.5 6l-6 6 6 6" />,
  info: <><path d={circle(12, 12, 8.5)} /><path d="M12 11v5.2M12 7.7v.1" /></>,
  alert: <><path d={circle(12, 12, 8.5)} /><path d="M12 7.6v5.2M12 16.2v.1" /></>,
  'sign-out': <path d="M14 4.5h3.5a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H14M10 16l-4-4 4-4M6 12h9.5" />,
  globe: <><path d={circle(12, 12, 8.5)} /><path d="M3.5 12h17M12 3.5c2.4 2.5 3.5 5.3 3.5 8.5s-1.1 6-3.5 8.5c-2.4-2.5-3.5-5.3-3.5-8.5s1.1-6 3.5-8.5z" /></>,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  refresh: <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3M19.5 4.5v4.2h-4.2" />,
  sidebar: <><path d={roundRect(3.5, 4.5, 17, 15, 2.4)} /><path d="M9.5 4.5v15" /></>,
  flag: <path d="M5.6 21V3.8M5.6 4h11.6l-2.2 4.2 2.2 4.2H5.6" />,
  sparkle: <path d="M12 3.5c.6 4.2 2.3 5.9 6.5 6.5-4.2.6-5.9 2.3-6.5 6.5-.6-4.2-2.3-5.9-6.5-6.5 4.2-.6 5.9-2.3 6.5-6.5z" />,
} satisfies Record<string, ReactNode>

export type IconName = keyof typeof ICONS
const FLIPPING: ReadonlySet<IconName> = new Set<IconName>(['chevron-end', 'chevron-start', 'arrow-end', 'arrow-start', 'sign-out'])

export interface IconProps {
  name: IconName
  size?: number
  label?: string
  className?: string
  /** Stroke weight; 2 by default, 2.5 for the 16px compact rows. */
  weight?: number
}

export function Icon({ name, size = 20, label, className, weight = 2 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={clsx('r5-icon', FLIPPING.has(name) && 'r5-icon--flip', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth={weight}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : 'true'}
      role={label ? 'img' : undefined}
      aria-label={label}
      focusable="false"
      data-icon={name}
    >
      {ICONS[name]}
    </svg>
  )
}
